import { Dialog, DialogActions, DialogContent, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { ButtonCancel, ButtonConfirm } from "./buttons";
import { MprcContainer } from "./MprcContainer";
import { MprcDialogTitleClose } from "./MprcDialogTitleClose";
import { ExtractErrorInformation, MprcRequestError } from "./MprcRequestErrorReport";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    title: string;
    placeholder?: string;
    value?: string;
    acceptEmptyValue?: boolean;
    readOnly?: boolean;
    maxLength?: number;
    onClose: () => void;
    onConfirm: (name?: string) => Promise<void>;
}

////////////////////////////////////////////////////////////////////////////

export const MprcSimpleEditDialog: React.FC<IProps> = (props) => {

    const { title, placeholder, maxLength = 255, acceptEmptyValue = false, readOnly = false, onClose, onConfirm } = props;

    const { enqueueSnackbar } = useSnackbar();

    const [text, setText] = useState<string | undefined>(props.value);
    const [loading, setLoading] = useState<boolean>(false);

    const isRecordChange = props.value != text;
    const inputError = text != undefined && acceptEmptyValue === false && text?.length === 0;

    const canSave = !inputError && !loading && isRecordChange && !readOnly;
    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleConfirm = async () => {
        if (inputError || loading || !isRecordChange || (text ?? "").length == 0) return;

        setLoading(true);
        try {
            await onConfirm(text);
        }
        catch (e) {
            let error = ExtractErrorInformation(e as MprcRequestError);
            enqueueSnackbar(error.message, { variant: "error" });
        }
        finally {
            setLoading(false);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return <Dialog
        open
        fullWidth
        maxWidth="sm"
        onClose={() => onClose()}
    >
        <MprcDialogTitleClose
            shrinkPadding
            title={title}
            onClose={() => onClose()}
        />

        <DialogContent>
            <MprcContainer margin="dense">
                <TextField
                    autoFocus
                    fullWidth
                    value={text ?? ""}
                    placeholder={placeholder}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.keyCode == 13 && canSave) handleConfirm(); }}
                    error={inputError}
                    disabled={loading}
                    autoComplete="off"
                    slotProps={{
                        input: {
                            inputProps: {
                                maxLength: maxLength,
                                readOnly: readOnly
                            }
                        }
                    }}
                />
            </MprcContainer>
        </DialogContent>
        <DialogActions>
            <ButtonCancel disabled={loading} variant="contained" onClick={onClose} />
            <ButtonConfirm
                disabled={!canSave}
                variant="contained"
                onClick={() => handleConfirm()}
            />
        </DialogActions>
    </Dialog>;
};
